/**
 * What one lane card shows: its colour, its words and its reason, from ONE
 * observation.
 *
 * They came from three. The colour was taken from the status the page loaded
 * with, the label from the test just pressed, and the reason fell back to the
 * page-load reason whenever the test had none. So a lane that had been down
 * and was now fine rendered "ok, 24 ms" in red, over a stale failure.
 *
 * The colour is the expensive part. An operator reads it before the text, and
 * red on TIM means the chain to BMW is down: an hour spent hunting an outage
 * that is not there. Twice, and they stop trusting the colour, which loses the
 * signal on the day it is real.
 *
 * A named function with its own test, rather than three expressions in the
 * markup, because that is the only way to ask it directly whether an ok lane
 * can ever carry the error signal.
 */

export type LaneStatus = 'ok' | 'degraded' | 'down' | 'unknown'

/** The `StatusBadge` tones this console uses. Only severity and connection
 *  state are ever coloured, because the eye reads colour first. */
export const LANE_TONE = {
  ok: 'success',
  degraded: 'warning',
  down: 'error',
  unknown: 'warning',
} as const

export interface LaneObservation {
  status?: LaneStatus | null
  detail?: string | null
}

export interface LaneAppearance {
  status: LaneStatus
  tone: (typeof LANE_TONE)[LaneStatus]
  label: string
  detail: string | null
}

/**
 * `tested` is the result of the Test button and wins outright when present:
 * pressing Test asks about now, so nothing from before it may survive
 * alongside. Including its reason, especially when that reason is nothing.
 */
export function laneAppearance(
  loaded: LaneObservation,
  tested?: { status?: LaneStatus | null; label?: string; detail?: string | null },
): LaneAppearance {
  const source = tested ?? loaded
  const status = (source.status ?? 'unknown') as LaneStatus
  return {
    status,
    tone: LANE_TONE[status] ?? LANE_TONE.unknown,
    label: tested?.label ?? status,
    // Nothing rather than an empty label. A line ending in a colon with
    // nothing after it reads as something lost, which is worse than a line
    // that is not there.
    detail: source.detail ? source.detail : null,
  }
}
